import { Box, Stack, Typography } from '@mui/material';
import { ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { AddButton } from '@/components/buttons/addButton';
import { SelectionPanel } from '@/components/panels/selectionPanel/selectionPanel';
import { SearchTextBox } from '@/components/search/searchTextBox';
import { useDAL } from '@/contexts/useDAL';
import {
  createCombineCondition,
  isValueCondition,
  PropertyType,
} from '@/types/advanceFilter';

import type { TagsFilterProps } from './tagsFilter';

import { MultiSelectFilter } from '../filters';
import { cloudProvider } from '../properties';

export const TagsCondition = (props: TagsFilterProps) => {
  const dal = useDAL();
  const [keyFilter, setKeyFilter] = useState('');
  const [valueFilter, setValueFilter] = useState('');

  const [isScrolledVertically, setScrolledVertically] = useState(false);
  const filter = useMemo(() => {
    if (!props.supportedProviders?.length) {
      return undefined;
    }
    return createCombineCondition([
      {
        property: cloudProvider.name,
        operator: ConditionOperatorEnum.In,
        value: props.supportedProviders,
      },
    ]);
  }, [props.supportedProviders]);
  const { data: body, isLoading } = dal.inventory.tags.useList(filter);

  const condition = props.conditions.find(
    (x) => isValueCondition(x) && x.property === props.property.name
  );

  const selectedResources = useMemo(
    () => (isValueCondition(condition) && condition?.value) || [],
    [condition]
  );

  const allTags = useMemo(() => {
    const tags = body?.tags || [];

    selectedResources.forEach((tag) => {
      const [key, value] = tag.split('=');
      if (!tags.some((t) => t.key === key && t.value === value)) {
        tags.push({ key, value });
      }
    });

    return tags;
  }, [body, selectedResources]);

  const tagsReduced = useMemo(
    () =>
      allTags.reduce(
        (acc, tag) => {
          {
            acc.keys.add(tag.key);
            if (tag.value) {
              acc.values.add(tag.value);
            }
            return acc;
          }
        },
        { keys: new Set<string>(), values: new Set<string>() }
      ),
    [allTags]
  );

  const keys = useMemo(
    () =>
      Array.from(tagsReduced.keys)
        .filter(
          (key) =>
            !keyFilter || key.toLowerCase().includes(keyFilter.toLowerCase())
        )
        .map((key) => ({
          id: key,
          name: key,
        })),
    [keyFilter, tagsReduced.keys]
  );

  const values = useMemo(
    () =>
      Array.from(tagsReduced.values)
        .filter(
          (value) =>
            !valueFilter ||
            value.toLowerCase().includes(valueFilter.toLowerCase())
        )
        .sort()
        .map((value) => ({
          id: value,
          name: value,
        })),
    [valueFilter, tagsReduced.values]
  );

  const keyValuePairs = useMemo(() => {
    const allValues = Array.from(
      new Set(
        allTags
          .filter(
            (t) =>
              (!keyFilter ||
                t.key.toLowerCase().includes(keyFilter.toLowerCase())) &&
              (!valueFilter ||
                !!t.value?.toLowerCase().includes(valueFilter.toLowerCase()))
          )
          .map((x) => `${x.key}=${x.value || ''}`)
      )
    );

    return allValues.map((value) => ({
      id: value,
      name: value,
    }));
  }, [allTags, keyFilter, valueFilter]);

  const filteredValue =
    props.initialFilterMode === 'key'
      ? keyFilter
      : props.initialFilterMode === 'value'
        ? valueFilter
        : `${keyFilter}=${valueFilter}`;

  const options =
    props.initialFilterMode === 'key'
      ? keys
      : props.initialFilterMode === 'value'
        ? values
        : keyValuePairs;

  return (
    <SelectionPanel
      isLoading={isLoading}
      drawerProps={{
        items: selectedResources,
        entityType: 'tag',
        onRemove: (item) => {
          const values = selectedResources.filter((x) => x !== item);
          const existingCondition = props.conditions.find(
            (x) => isValueCondition(x) && x.property === props.property.name
          );
          props.onChange(
            props.conditions
              .filter((x) => x !== existingCondition)
              .concat(
                values.length
                  ? [
                      {
                        property: props.property.name,
                        operator: existingCondition
                          ? existingCondition.operator
                          : props.property.type === PropertyType.MultipleValues
                            ? ConditionOperatorEnum.ContainsAnyOf
                            : ConditionOperatorEnum.In,
                        value: values,
                      },
                    ]
                  : []
              )
          );
        },
        renderItem: (item) => <Typography>{item}</Typography>,
        itemKey: (item) => item,
        clearAll: () =>
          props.onChange(props.conditions.filter((x) => x !== condition)),
      }}
    >
      <Stack
        gap='12px'
        className='px-[24px] pb-[24px] py-[12px]'
        sx={{
          borderBottom: '1px solid var(--mui-palette-divider)',
          boxShadow: isScrolledVertically
            ? 'var(--mui-customShadows-panel)'
            : undefined,
        }}
      >
        <Typography className='my-[12px]' variant='body1'>
          {`Select one or more ${props.initialFilterMode}${props.initialFilterMode === 'key' ? 's' : ' pairs'}.`}
        </Typography>
        {(props.initialFilterMode.includes('key') ||
          props.initialFilterMode.includes('value')) && (
          <Stack
            direction='row'
            alignItems='center'
            gap='12px'
            className='w-full'
          >
            <Stack gap='12px' flexGrow={1}>
              {props.initialFilterMode.includes('key') && (
                <SearchTextBox
                  value={keyFilter}
                  onChange={setKeyFilter}
                  placeholder='Search by key'
                />
              )}
              {props.initialFilterMode.includes('value') && (
                <SearchTextBox
                  value={valueFilter}
                  onChange={setValueFilter}
                  placeholder='Search by value'
                />
              )}
            </Stack>
            <AddButton
              disabled={
                selectedResources.includes(filteredValue) ||
                (props.initialFilterMode === 'key' && !keyFilter) ||
                (props.initialFilterMode === 'value' && !valueFilter) ||
                (props.initialFilterMode === 'key-value' && !keyFilter)
              }
              onClick={() => {
                const values = [...selectedResources, filteredValue];
                const existingCondition = props.conditions.find(
                  (x) =>
                    isValueCondition(x) && x.property === props.property.name
                );
                props.onChange(
                  props.conditions
                    .filter((x) => x !== existingCondition)
                    .concat(
                      values.length
                        ? [
                            {
                              property: props.property.name,
                              operator: existingCondition
                                ? existingCondition.operator
                                : props.property.type ===
                                    PropertyType.MultipleValues
                                  ? ConditionOperatorEnum.ContainsAnyOf
                                  : ConditionOperatorEnum.In,
                              value: values,
                            },
                          ]
                        : []
                    )
                );
              }}
            />
          </Stack>
        )}
      </Stack>
      <Box
        flexGrow={1}
        className='overflow-auto px-[24px] py-[12px]'
        onScroll={(event) => {
          const target = event.target as HTMLDivElement;
          if (typeof target.scrollTop === 'number') {
            const isScrolled = target.scrollTop > 0;
            if (isScrolled !== isScrolledVertically) {
              setScrolledVertically(isScrolled);
            }
          }
        }}
      >
        {options.length === 0 && (
          <Typography variant='body1' className='my-[12px]'>
            {`No matching values found. Try a new search term, or add this value manually to include it once it’s discovered.`}
          </Typography>
        )}
        {options.length > 0 && (
          <MultiSelectFilter
            {...props}
            options={options}
            virtuosoStyles={{
              height: selectedResources.length
                ? props.initialFilterMode === 'key-value'
                  ? '226px'
                  : '270px'
                : props.initialFilterMode === 'key-value'
                  ? '286px'
                  : '330px',
              marginRight: '-24px',
              paddingRight: '24px',
            }}
          />
        )}
      </Box>
    </SelectionPanel>
  );
};
